import React, { useState } from "react";
import s from "./WhoPlaysSheet.module.scss";

// /debug/who-plays: ten ways the "Who's playing?" card could look (the step
// before Play Now and a game room). Mock players; every card works on its own
// (pick alone or several, add and remove) so each can be tried in both states.
const POOL = [
  { name: "LuckyLuke", color: "#03a9f4", l: "←", r: "→" },
  { name: "Erik", color: "#e91e63", l: "A", r: "D" },
  { name: "Player 3", color: "#4caf50", l: "Y", r: "U" },
  { name: "Player 4", color: "#ff9800", l: "V", r: "B" },
  { name: "Player 5", color: "#9c27b0", l: "K", r: "L" },
  { name: "Player 6", color: "#cddc39", l: "1", r: "2" },
];
type P = (typeof POOL)[number];
const SOLO = "Just me, myself and I", MANY = "Multiple keyboard warriors";

function useMock(start = 1) {
  const [n, setN] = useState(start);
  return {
    n, players: POOL.slice(0, n), many: n > 1,
    solo: () => setN(1), several: () => setN((v) => Math.max(2, v)),
    add: () => setN((v) => Math.min(6, v + 1)), remove: () => setN((v) => Math.max(1, v - 1)), set: setN,
  };
}
type M = ReturnType<typeof useMock>;

const Keys = ({ p, cls = "" }: { p: P; cls?: string }) => (<span className={`${s.keys} ${cls}`}><kbd>{p.l}</kbd><kbd>{p.r}</kbd></span>);
const Dot = ({ p }: { p: P }) => <i className={s.dot} style={{ background: p.color }} />;
const X = ({ m }: { m: M }) => <button type="button" className={s.x} onClick={m.remove} aria-label="Remove">×</button>;
const Go = ({ cls = "" }: { cls?: string }) => <button type="button" className={`${s.go} ${cls}`}>PLAY</button>;
const Worm = ({ color, w = 120 }: { color: string; w?: number }) => (
  <svg viewBox="0 0 120 40" width={w} height={(w / 120) * 40} aria-hidden="true">
    <path d="M4 30 C 24 4, 44 38, 66 20 S 96 8, 108 18" fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" />
    <circle cx="109" cy="18" r="6.5" fill="#ffb74d" />
  </svg>
);
const Row = ({ p, m, i }: { p: P; m: M; i: number }) => (
  <div className={s.row}><Dot p={p} /><span className={s.nm}>{p.name}</span><Keys p={p} />{m.n > 2 && i > 0 ? <X m={m} /> : null}</div>
);
const Tabs = ({ m, cls = "" }: { m: M; cls?: string }) => (
  <div className={`${s.tabs} ${cls}`}>
    <button type="button" className={!m.many ? s.on : undefined} onClick={m.solo}>{SOLO}</button>
    <button type="button" className={m.many ? s.on : undefined} onClick={m.several}>{MANY}</button>
  </div>
);
const Add = ({ m, cls = "" }: { m: M; cls?: string }) => (m.many && m.n < 6 ? <button type="button" className={`${s.add} ${cls}`} onClick={m.add}>+ Add player</button> : null);

// 1. As it is now.
function D1() { const m = useMock(2); return (
  <div className={`${s.card} ${s.d1}`}><h3>Who's playing?</h3><Tabs m={m} />{m.players.map((p, i) => <Row key={i} p={p} m={m} i={i} />)}<Add m={m} /><Go /></div>
); }

// 2. Two big picture tiles; the roster opens under the one you pick.
function D2() { const m = useMock(1); return (
  <div className={`${s.card} ${s.d2}`}>
    <h3>Who's playing?</h3>
    <div className={s.tiles}>
      <button type="button" className={!m.many ? s.on : undefined} onClick={m.solo}><span className={s.art}><Worm color="#03a9f4" w={130} /></span><b>{SOLO}</b></button>
      <button type="button" className={m.many ? s.on : undefined} onClick={m.several}><span className={s.art}><Worm color="#e91e63" w={86} /><Worm color="#4caf50" w={86} /><Worm color="#ff9800" w={86} /></span><b>{MANY}</b></button>
    </div>
    {m.many && m.players.map((p, i) => <Row key={i} p={p} m={m} i={i} />)}
    <Add m={m} /><Go />
  </div>
); }

// 3. Character select: a tall card per player, an empty one to add. No tabs.
function D3() { const m = useMock(2); return (
  <div className={`${s.card} ${s.d3}`}>
    <h3>Who's playing?</h3>
    <div className={s.seats}>
      {m.players.map((p, i) => (
        <div key={i} className={s.seat} style={{ ["--c" as any]: p.color }}>
          {i > 0 && i === m.n - 1 ? <X m={m} /> : null}
          <i className={s.head} /><b>{p.name}</b><Keys p={p} />
        </div>
      ))}
      {m.n < 6 && <button type="button" className={s.empty} onClick={m.add}><span>+</span></button>}
    </div>
    <Go />
  </div>
); }

// 4. The keyboard itself: each player's two keys lit in their colour.
const KB = ["1234567890", "QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"];
function D4() { const m = useMock(3);
  const lit = (k: string) => m.players.find((p) => p.l === k || p.r === k);
  const arrows = m.players.find((p) => p.l === "←");
  return (
  <div className={`${s.card} ${s.d4}`}>
    <h3>Who's playing?</h3>
    <Tabs m={m} />
    <div className={s.kb}>
      {KB.map((line, r) => (
        <div key={r} style={{ paddingLeft: r * 11 }}>
          {line.split("").map((k) => { const p = lit(k); return <kbd key={k} style={p ? { background: p.color, color: "#04121c", borderColor: p.color } : undefined}>{k}</kbd>; })}
          {r === 3 && <span className={s.arrows}>{["←", "→"].map((k) => <kbd key={k} style={arrows ? { background: arrows.color, color: "#04121c", borderColor: arrows.color } : undefined}>{k}</kbd>)}</span>}
        </div>
      ))}
    </div>
    <div className={s.legend}>{m.players.map((p, i) => <span key={i}><Dot p={p} />{p.name}</span>)}{m.many && m.n < 6 ? <button type="button" onClick={m.add}>+</button> : null}</div>
    <Go />
  </div>
); }

// 5. Slim: no boxes, a colour bar per line, underlined tabs.
function D5() { const m = useMock(2); return (
  <div className={`${s.card} ${s.d5}`}>
    <h3>Who's playing?</h3>
    <Tabs m={m} cls={s.under} />
    {m.players.map((p, i) => (<div key={i} className={s.line} style={{ ["--c" as any]: p.color }}><span className={s.nm}>{p.name}</span><Keys p={p} />{m.n > 2 && i > 0 ? <X m={m} /> : null}</div>))}
    <Add m={m} cls={s.addText} /><Go />
  </div>
); }

// 6. A number, not tabs: how many, with minus and plus.
function D6() { const m = useMock(1); return (
  <div className={`${s.card} ${s.d6}`}>
    <div className={s.left}>
      <small>Who's playing?</small>
      <div className={s.stepper}><button type="button" onClick={m.remove} disabled={m.n <= 1}>−</button><b>{m.n}</b><button type="button" onClick={m.add} disabled={m.n >= 6}>+</button></div>
      <em>{m.many ? MANY : SOLO}</em>
    </div>
    <div className={s.right}>{m.players.map((p, i) => <Row key={i} p={p} m={{ ...m, n: 0 }} i={i} />)}<Go /></div>
  </div>
); }

// 7. Worm lanes: each player is a worm in their colour.
function D7() { const m = useMock(3); return (
  <div className={`${s.card} ${s.d7}`}>
    <h3>Who's playing?</h3><Tabs m={m} />
    {m.players.map((p, i) => (<div key={i} className={s.lane}><Worm color={p.color} w={104} /><span className={s.nm}>{p.name}</span><Keys p={p} />{m.n > 2 && i > 0 ? <X m={m} /> : null}</div>))}
    <Add m={m} /><Go />
  </div>
); }

// 8. Arcade: six slots, P1 to P6. Press a free one to join.
function D8() { const m = useMock(2); return (
  <div className={`${s.card} ${s.d8}`}>
    <h3>WHO'S PLAYING?</h3>
    <div className={s.slots}>
      {POOL.map((p, i) => i < m.n ? (
        <button type="button" key={i} className={s.slotOn} style={{ ["--c" as any]: p.color }} onClick={() => i > 0 && m.set(i)}><small>P{i + 1}</small><b>{p.name}</b><Keys p={p} /></button>
      ) : (
        <button type="button" key={i} className={s.slotOff} onClick={() => m.set(i + 1)}><small>P{i + 1}</small><b>JOIN</b></button>
      ))}
    </div>
    <Go />
  </div>
); }

// 9. A strip: players as chips in a row, Play at the end.
function D9() { const m = useMock(3); return (
  <div className={`${s.card} ${s.d9}`}>
    <small>Who's playing?</small>
    <div className={s.strip}>
      {m.players.map((p, i) => (<span key={i} className={s.chip} style={{ ["--c" as any]: p.color }}><Dot p={p} /><b>{p.name}</b><Keys p={p} />{i > 0 && i === m.n - 1 ? <X m={m} /> : null}</span>))}
      {m.n < 6 && <button type="button" className={s.plus} onClick={m.add}>+</button>}
      <Go />
    </div>
  </div>
); }

// 10. The button's own colours on top, then: how many? 1 to 6.
function D10() { const m = useMock(1); return (
  <div className={`${s.card} ${s.d10}`}>
    <div className={s.band}><b>WHO'S PLAYING?</b></div>
    <div className={s.inner}>
      <div className={s.count}>{[1, 2, 3, 4, 5, 6].map((k) => <button type="button" key={k} className={k === m.n ? s.on : undefined} onClick={() => m.set(k)}>{k}</button>)}</div>
      {m.players.map((p, i) => <Row key={i} p={p} m={{ ...m, n: 0 }} i={i} />)}
      <Go />
    </div>
  </div>
); }

// ---- Variations of 8, with names and colours you can change ----
const PALETTE = ["#03a9f4", "#e91e63", "#4caf50", "#ff9800", "#9c27b0", "#cddc39", "#3f51b5", "#009688", "#f44336", "#00e5ff", "#607d8b", "#ffeb3b"];
function useSlots(start = 2) {
  const [n, setN] = useState(start);
  const [ps, setPs] = useState(() => POOL.map((p) => ({ ...p })));
  const [open, setOpen] = useState<number | null>(null); // whose colours are open
  const patch = (i: number, c: Partial<P>) => setPs((cur) => cur.map((p, k) => (k === i ? { ...p, ...c } : p)));
  return {
    n, ps, open, setOpen,
    fillTo: (k: number) => { setN(Math.max(1, Math.min(6, k))); setOpen(null); },
    // taking a player out moves the ones after it up a slot
    remove: (i: number) => { setPs((cur) => [...cur.slice(0, i), ...cur.slice(i + 1), cur[i]]); setN((v) => Math.max(1, v - 1)); setOpen(null); },
    name: (i: number, name: string) => patch(i, { name: name.slice(0, 16) }),
    color: (i: number, color: string) => { patch(i, { color }); setOpen(null); },
  };
}
type S = ReturnType<typeof useSlots>;
// The colour button of slot `i`, with its colours dropping down over the card.
function Colour({ m, i, cls = "" }: { m: S; i: number; cls?: string }) {
  const taken = m.ps.slice(0, m.n).filter((_, k) => k !== i).map((p) => p.color);
  return (
    <span className={`${s.colour} ${cls}`}>
      <button type="button" className={s.dot} style={{ background: m.ps[i].color }} onClick={() => m.setOpen(m.open === i ? null : i)} aria-label="Colour" />
      {m.open === i && (
        <span className={s.pal}>{PALETTE.map((c) => <button type="button" key={c} style={{ background: c }} disabled={taken.includes(c)} className={c === m.ps[i].color ? s.palOn : undefined} onClick={() => m.color(i, c)} aria-label={c} />)}</span>
      )}
    </span>
  );
}
const NameIn = ({ m, i }: { m: S; i: number }) => (<input className={s.nameIn} value={m.ps[i].name} maxLength={16} onChange={(e) => m.name(i, e.target.value)} aria-label={`Player ${i + 1} name`} spellCheck={false} />);
const Out = ({ m, i }: { m: S; i: number }) => (i > 0 ? <button type="button" className={`${s.x} ${s.out}`} onClick={() => m.remove(i)} aria-label="Remove">×</button> : null);

// 8A. The grid of six: the colour on top (no P1..P6), then the name and the keys.
function D8A({ cls = "" }: { cls?: string }) { const m = useSlots(2); return (
  <div className={`${s.card} ${s.d8} ${s.arcA} ${cls}`}>
    <h3>WHO'S PLAYING?</h3>
    <div className={s.slots}>
      {m.ps.map((p, i) => i < m.n ? (
        <div key={i} className={s.slotOn} style={{ ["--c" as any]: p.color }}><Out m={m} i={i} /><Colour m={m} i={i} cls={s.mid} /><NameIn m={m} i={i} /><Keys p={p} /></div>
      ) : (
        <button type="button" key={i} className={s.slotOff} onClick={() => m.fillTo(i + 1)} aria-label="Add player"><b className={s.plusBig}>+</b></button>
      ))}
    </div>
    <Go />
  </div>
); }

// 8B. Six tall slots in one row: a band of the player's colour on top.
function D8B() { const m = useSlots(3); return (
  <div className={`${s.card} ${s.arcB}`}>
    <h3>WHO'S PLAYING?</h3>
    <div className={s.cols}>
      {m.ps.map((p, i) => i < m.n ? (
        <div key={i} className={s.col} style={{ ["--c" as any]: p.color }}>
          <span className={s.colTop}><b>P{i + 1}</b><Out m={m} i={i} /></span>
          <Colour m={m} i={i} cls={s.big} /><NameIn m={m} i={i} /><Keys p={p} />
        </div>
      ) : (
        <button type="button" key={i} className={s.colOff} onClick={() => m.fillTo(i + 1)}><b>P{i + 1}</b><span>+</span></button>
      ))}
    </div>
    <Go />
  </div>
); }

// 8C. Two columns of wide slots: colour, name, keys (no numbers).
function D8C({ cls = "" }: { cls?: string }) { const m = useSlots(2); return (
  <div className={`${s.card} ${s.arcC} ${cls}`}>
    <h3>WHO'S PLAYING?</h3>
    <div className={s.wides}>
      {m.ps.map((p, i) => i < m.n ? (
        <div key={i} className={s.wide} style={{ ["--c" as any]: p.color }}><Colour m={m} i={i} cls={s.mid} /><NameIn m={m} i={i} /><Keys p={p} /><Out m={m} i={i} /></div>
      ) : (
        <button type="button" key={i} className={s.wideOff} onClick={() => m.fillTo(i + 1)} aria-label="Add player"><b className={s.plusBig}>+</b></button>
      ))}
    </div>
    <Go />
  </div>
); }

// 8D. Taken slots are solid in the player's colour; a free slot lights up under the pointer.
function D8D() { const m = useSlots(3); return (
  <div className={`${s.card} ${s.d8} ${s.arcD}`}>
    <h3>WHO'S PLAYING?</h3>
    <div className={s.slots}>
      {m.ps.map((p, i) => i < m.n ? (
        <div key={i} className={s.solid} style={{ ["--c" as any]: p.color }}><Out m={m} i={i} /><small>P{i + 1}</small><NameIn m={m} i={i} /><span className={s.who}><Colour m={m} i={i} /><Keys p={p} /></span></div>
      ) : (
        <button type="button" key={i} className={s.slotOff} onClick={() => m.fillTo(i + 1)}><small>P{i + 1}</small><b>+</b></button>
      ))}
    </div>
    <Go />
  </div>
); }

// 8E. One full row per player, and one plus row under the last.
function D8E({ cls = "" }: { cls?: string }) { const m = useSlots(2); return (
  <div className={`${s.card} ${s.arcC} ${s.arcE} ${cls}`}>
    <h3>WHO'S PLAYING?</h3>
    <div className={s.rowsE}>
      {m.ps.slice(0, m.n).map((p, i) => (
        <div key={i} className={s.wide} style={{ ["--c" as any]: p.color }}><Colour m={m} i={i} cls={s.mid} /><NameIn m={m} i={i} /><Keys p={p} /><Out m={m} i={i} /></div>
      ))}
      {m.n < 6 && <button type="button" className={s.wideOff} onClick={() => m.fillTo(m.n + 1)} aria-label="Add player"><b className={s.plusBig}>+</b></button>}
    </div>
    <Go />
  </div>
); }

const MORE: [string, () => React.ReactElement][] = [
  ["8A, roomy", () => <D8A cls={s.roomyA} />],
  ["8A, roomier: taller slots, bigger colour", () => <D8A cls={s.roomierA} />],
  ["8C, roomy", () => <D8C cls={s.roomyC} />],
  ["8C, roomier: tall rows, bigger name", () => <D8C cls={s.roomierC} />],
  ["8E. A full row per player, one plus under", () => <D8E />],
  ["8E, roomy", () => <D8E cls={s.roomyE} />],
];

const ARCADE: [string, () => React.ReactElement][] = [
  ["8A. The grid: the colour on top, no P1 to P6", () => <D8A />],
  ["8B. Six tall slots in a row", D8B],
  ["8C. Two columns of wide slots, no numbers", () => <D8C />],
  ["8D. Taken slots solid in the player's colour", D8D],
];

const ALL: [string, () => React.ReactElement][] = [
  ["1. As it is now", D1],
  ["2. Two picture tiles", D2],
  ["3. A card per player, an empty one to add (no tabs)", D3],
  ["4. The keyboard, each player's keys lit", D4],
  ["5. Slim: lines, no boxes", D5],
  ["6. A number with minus and plus", D6],
  ["7. Worm lanes", D7],
  ["8. Arcade slots, P1 to P6", D8],
  ["9. A strip of chips", D9],
  ["10. The button's colours on top, pick 1 to 6", D10],
];

export default function WhoPlaysSheet() {
  return (
    <div className={s.page}>
      <h1>Who's playing?</h1>
      <div className={s.grid}>{MORE.map(([title, D]) => (<section key={title}><h2>{title}</h2><D /></section>))}</div>
      <h1 style={{ marginTop: 44 }}>The four arcade variations</h1>
      <div className={s.grid}>{ARCADE.map(([title, D]) => (<section key={title}><h2>{title}</h2><D /></section>))}</div>
      <h1 style={{ marginTop: 44 }}>The first ten</h1>
      <div className={s.grid}>{ALL.map(([title, D]) => (<section key={title}><h2>{title}</h2><D /></section>))}</div>
    </div>
  );
}
